// components/schematics/schematics-init.js
import { initAggregateHelper, registerAggregate, handleValueUpdate } from "../../services/aggregateHelper.ts";
import { fetchSchematics, getSvg } from "../../services/api.ts";
import { subscribeRegister, sendCommand } from "../../services/mqttClient.ts";


export async function initSchematics(siteId, registers, containerSelector = ".schematics-canvas") {
  const container = document.querySelector(containerSelector);
  if (!container) {
    console.error("[Schematics] Container not found:", containerSelector);
    return;
  }

  container.innerHTML = `<div class="schematic-loading">Loading schematic...</div>`;
  const schematics = await fetchSchematics(siteId);
  if (!schematics?.length) {
    container.innerHTML = `<p style="color:#aaa;text-align:center;padding:80px;font-size:18px">No schematic found for this site.</p>`;
    return;
  }

  // Case-insensitive register lookup
  const registersByName = {};
  registers.forEach(r => {
    if (r.name) registersByName[r.name.toLowerCase()] = r;
  });

  const findBestRegister = (label: string) => {
    const direct = registersByName[label.toLowerCase()];
    if (direct) return direct;
    const norm = label.toLowerCase().replace(/[_\s]/g, '');
    return registers.find(r => {
      const name = (r.name || '').toLowerCase();
      const compact = name.replace(/[_\s]/g, '');
      return name.includes(label.toLowerCase()) || norm.includes(compact) || compact.includes(norm);
    });
  };

  const formatRegValue = (reg: any, raw: any) => {
    if (raw == null || typeof raw !== 'number') return 'N/A';
    const unit = reg?.unit || reg?.meta?.unit || '';
    return `${raw.toFixed(2).replace(/\.?0+$/, '')}${unit ? ' ' + unit : ''}`;
  };

  let svgText;
  try {
    svgText = await (await getSvg(schematics[0].svgUrl)).text();
  } catch (err) {
    console.error("[Schematics] Failed to load SVG:", err);
    container.innerHTML = `<p style="color:#f44;text-align:center;padding:80px">Failed to load schematic.</p>`;
    return;
  }

  container.insertAdjacentHTML('beforeend', svgText);
  container.querySelector('.schematic-loading')?.remove();
  const svgRoot = container.querySelector("svg");
  if (!svgRoot) {
    console.error("[Schematics] SVG root not found");
    return;
  }

  // === AGGREGATE HELPER (e.g. BESS average SOC) ===
  const updateSvgLabel = (labelName, value) => {
    const formatted = typeof value === 'number' ? value.toFixed(2).replace(/\.?0+$/, "") + "%" : value;
    const cp = svgRoot.querySelector(`v\\:cp[v\\:lbl="${labelName}"]`);
    if (!cp) return;
    const group = cp.closest("g[id^='shape']");
    if (!group) return;
    const textEl = group.querySelectorAll("text")[group.querySelectorAll("text").length - 1];
    if (textEl) {
      textEl.textContent = formatted;
      textEl.style.fill = "#FFD700";
      textEl.style.fontWeight = "bold";
    }
  };
  initAggregateHelper(updateSvgLabel);
  registerAggregate('bess_soc_average', 'average', ['bess_soc']);

  // === RESPONSIVE + ZOOM/PAN ===
  svgRoot.style.width = "100%";
  svgRoot.style.height = "100%";
  svgRoot.style.display = "block";
  svgRoot.style.background = "#1e1e1e";
  svgRoot.style.touchAction = "none";
  svgRoot.style.overflow = "hidden";

  if (!svgRoot.hasAttribute("viewBox")) {
    const w = svgRoot.getAttribute("width") || "1548";
    const h = svgRoot.getAttribute("height") || "612";
    svgRoot.setAttribute("viewBox", `0 0 ${w} ${h}`);
  }
  svgRoot.removeAttribute("width");
  svgRoot.removeAttribute("height");

  const zoomGroup = document.createElementNS("http://www.w3.org/2000/svg", "g");
  zoomGroup.id = "zoom-group";
  while (svgRoot.firstChild) zoomGroup.appendChild(svgRoot.firstChild);
  svgRoot.appendChild(zoomGroup);

  let scale = 1, tx = 0, ty = 0, isPanning = false, startX = 0, startY = 0;
  const updateTransform = () => zoomGroup.setAttribute("transform", `translate(${tx} ${ty}) scale(${scale})`);

  container.addEventListener("wheel", e => {
    e.preventDefault();
    const rect = container.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    const newScale = Math.max(0.2, Math.min(20, scale * delta));
    const ratio = newScale / scale;
    tx = x - (x - tx) * ratio;
    ty = y - (y - ty) * ratio;
    scale = newScale;
    updateTransform();
  }, { passive: false });

  const pointerDown = e => { if (e.button === 0) { isPanning = true; startX = e.clientX - tx; startY = e.clientY - ty; container.style.cursor = "grabbing"; } };
  container.addEventListener("pointerdown", pointerDown);
  container.addEventListener("pointermove", e => { if (isPanning) { tx = e.clientX - startX; ty = e.clientY - startY; updateTransform(); } });
  const pointerUp = () => { isPanning = false; container.style.cursor = "grab"; };
  container.addEventListener("pointerup", pointerUp);
  container.addEventListener("pointerleave", pointerUp);
  container.addEventListener("dblclick", () => { scale = 1; tx = ty = 0; updateTransform(); });

  let touchDist = 0;
  container.addEventListener("touchstart", e => { if (e.touches.length === 2) { e.preventDefault(); const [a, b] = e.touches; touchDist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY); } }, { passive: false });
  container.addEventListener("touchmove", e => {
    if (e.touches.length !== 2 || touchDist === 0) return;
    e.preventDefault();
    const [a, b] = e.touches;
    const dist = Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY);
    const centerX = (a.clientX + b.clientX) / 2;
    const centerY = (a.clientY + b.clientY) / 2;
    const ratio = dist / touchDist;
    const newScale = Math.max(0.2, Math.min(20, scale * ratio));
    const scaleRatio = newScale / scale;
    tx = centerX - (centerX - tx) * scaleRatio;
    ty = centerY - (centerY - ty) * scaleRatio;
    scale = newScale;
    touchDist = dist;
    updateTransform();
  }, { passive: false });

  const resetBtn = Object.assign(document.createElement("button"), {
    textContent: "Reset View",
    onclick: () => { scale = 1; tx = ty = 0; updateTransform(); },
    style: `position:absolute;top:12px;right:12px;z-index:1000;padding:8px 16px;background:#333;color:white;border:none;border-radius:6px;cursor:pointer;font-size:13px;box-shadow:0 2px 8px rgba(0,0,0,0.3);`
  });
  container.style.position = "relative";
  container.appendChild(resetBtn);

  // === MODAL UI ===
  container.insertAdjacentHTML('beforeend', `
    <div id="schematic-modal" style="display:none;position:fixed;top:50%;left:50%;transform:translate(-50%,-50%);width:90%;max-width:420px;background:#1e1e1e;color:white;border-radius:12px;box-shadow:0 10px 40px rgba(0,0,0,0.8);z-index:10000;overflow:hidden;font-family:Segoe UI,Arial,sans-serif;">
      <div style="padding:16px;background:#2d2d2d;position:relative;">
        <h3 id="modal-title" style="margin:0;font-size:18px;">Component</h3>
        <button onclick="document.getElementById('schematic-modal').style.display='none'" style="position:absolute;top:12px;right:12px;background:none;border:none;color:#aaa;font-size:24px;cursor:pointer;">×</button>
      </div>
      <div style="padding:16px;">
        <div class="modal-tabs" style="display:flex;border-bottom:1px solid #444;margin-bottom:16px;">
          <button class="tab-btn active" data-tab="states" style="flex:1;padding:10px;background:none;border:none;color:white;font-size:14px;">States</button>
          <button class="tab-btn" data-tab="controls" style="flex:1;padding:10px;background:none;border:none;color:#888;font-size:14px;display:none;">Controls</button>
        </div>
        <div id="tab-states"></div>
        <div id="tab-controls" style="display:none;"></div>
      </div>
    </div>
    <div id="modal-backdrop" style="display:none;position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:9999;"></div>
  `);

  const modal = document.getElementById('schematic-modal');
  const backdrop = document.getElementById('modal-backdrop');
  const tabStates = document.getElementById('tab-states');
  const tabControls = document.getElementById('tab-controls');

  document.querySelectorAll('.tab-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.tab-btn').forEach(b => { b.classList.remove('active'); b.style.color = '#888'; });
      btn.classList.add('active');
      btn.style.color = 'white';
      tabStates.style.display = btn.dataset.tab === 'states' ? 'block' : 'none';
      tabControls.style.display = btn.dataset.tab === 'controls' ? 'block' : 'none';
    });
  });

  backdrop.addEventListener('click', () => { modal.style.display = 'none'; backdrop.style.display = 'none'; });

  // === GLOBAL MQTT COMMAND SENDER ===
  window.sendMqttCommand = (registerId, deviceId, action) => {
    if (!globalState?.currentSite?._id) return alert("Site not loaded");
    sendCommand(globalState.currentSite._id, deviceId, registerId, action);
  };

  // === MAIN BINDING LOOP (UPDATED FOR DG + SWITCHES) ===
  const subscriptions = [];

  // Process every group that could be clickable
  for (const group of svgRoot.querySelectorAll("g[id^='shape'], g[v\\:groupcontext='group']")) {
    const labelCp = group.querySelector("v\\:cp[v\\:lbl]");
    const titleText = group.querySelector("text")?.textContent.trim() || "";
    const hasOscillator = group.querySelector("ellipse") !== null;
    const isDG = hasOscillator && /^G\d+/i.test(titleText);
    const isSwitch = labelCp && /switch|earthing/i.test(labelCp.getAttribute("v:lbl") || "");

    if (!isDG && !isSwitch) {
      // Keep your original behavior for everything else
      const cp = group.querySelector('v\\:cp');
      if (cp) {
        const label = cp.getAttribute("v:lbl")?.trim();
        if (label) {
          let register = findBestRegister(label);

          const relatedRegisters = registers.filter(r => {
            const name = r.name.toLowerCase();
            const lbl = label.toLowerCase();
            return name.includes(lbl) || lbl.includes(name.replace(/[_ ]/g, ''));
          });
          const commandRegisters = relatedRegisters.filter(r => /start|stop|open|close|on|off|trip|reset/i.test(r.name));

          const texts = group.querySelectorAll("text");
          const displayTextEl = texts[texts.length - 1];
          const descEl = group.querySelector("desc");
          const shapeEls = group.querySelectorAll("path, rect, circle, ellipse, polygon");

          group.style.cursor = "pointer";
          group.addEventListener("click", e => {
            e.stopPropagation();
            document.getElementById("modal-title").textContent = label;

            tabStates.innerHTML = relatedRegisters.length
              ? relatedRegisters.map(reg => {
                  const val = formatRegValue(reg, reg.value).replace('N/A', '—');
                  return `<div style="padding:8px 0;border-bottom:1px solid #333;">
                    <div style="font-size:13px;color:#aaa;">${reg.name}</div>
                    <div style="font-size:18px;font-weight:bold;color:#00ff88;">${val}</div>
                  </div>`;
                }).join("")
              : "<p style='color:#aaa;text-align:center;'>No data</p>";

            if (commandRegisters.length > 0) {
              document.querySelector('[data-tab="controls"]').style.display = "block";
              tabControls.innerHTML = `
                <div style="padding:8px 0;font-size:14px;color:#aaa;text-align:center;margin-bottom:16px;">
                  Current status: <strong style="color:#00ff88;">${commandRegisters.some(r => r.value > 0) ? "Running" : "Stopped"}</strong>
                </div>
                ${commandRegisters.map(reg => {
                  const isOn = reg.value > 0;
                  const action = isOn ? 0 : 1;
                  const actionText = action === 1 ? "START" : "STOP";
                  const color = action === 1 ? "#4caf50" : "#f44336";
                  return `<button onclick="window.sendMqttCommand('${reg._id}', '${reg.deviceId}', ${action})"
                    style="width:100%;padding:14px;margin:8px 0;background:${color};color:white;border:none;border-radius:10px;font-size:16px;font-weight:bold;cursor:pointer;">
                    ${actionText} ${reg.name.replace(/_/g, ' ').toUpperCase()}
                  </button>`;
                }).join("")}
              `;
            } else {
              document.querySelector('[data-tab="controls"]').style.display = "none";
            }

            modal.style.display = "block";
            backdrop.style.display = "block";
          });

          if (register) {
            const formatValue = raw => formatRegValue(register, raw);
            const updateDisplay = value => {
              handleValueUpdate(register.dictionaryKey || register.name, value);
              const formatted = formatValue(value);
              if (displayTextEl) {
                displayTextEl.textContent = formatted;
                displayTextEl.style.fill = "#075e1db9";
                displayTextEl.style.fontWeight = "bold";
              }
              if (descEl) descEl.textContent = formatted;
              if (/status|switch|state|run|on|close|open|trip|alarm|fault/i.test(label) && shapeEls.length) {
                const isOn = value > 0;
                shapeEls.forEach(el => {
                  el.style.fill = isOn ? "#4caf50" : "#f44336";
                  el.style.stroke = isOn ? "#4caf50" : "#f44336";
                  el.style.transition = "all 0.3s";
                });
              }
            };
            if (register.value != null) updateDisplay(register.value);
            const unsub = subscribeRegister(siteId, register.deviceId, register._id, payload => {
              const value = payload?.value ?? payload;
              if (value != null) updateDisplay(value);
            });
            subscriptions.push(unsub);
          }
        }
      }
      continue;
    }

    // === SPECIAL HANDLING: DIESEL GENERATORS & SWITCHES ===
    group.style.cursor = "pointer";
    group.addEventListener("click", e => {
      e.stopPropagation();

      if (isDG) {
        const dgNumber = titleText.match(/\d+/)?.[0] || "1";
        document.getElementById("modal-title").textContent = `Diesel Generator ${dgNumber}`;
        document.querySelector('[data-tab="controls"]').style.display = "block";

        const dgRegisters = registers.filter(r => r.name.includes(`_${dgNumber}`) || r.name.endsWith(dgNumber));

        const states = [
          { label: "Communication status", keywords: ["communication", "status"], color: v => v ? "#4caf50" : "#f44336" },
          { label: "Fuel level alarm", keywords: ["fuel", "alarm"], color: v => v ? "#ff9800" : "#4caf50" },
          { label: "Generator availability", keywords: ["generator", "availability"], color: v => v ? "#4caf50" : "#f44336" },
          { label: "Generator grid mode", keywords: ["grid", "mode"], color: () => "#00bcd4" },
          { label: "Operational status", keywords: ["operational", "status"], color: () => "#00bcd4" },
          { label: "Setpoint Deviation Alarm", keywords: ["setpoint", "deviation", "alarm"], color: v => v ? "#f44336" : "#4caf50" },
        ];

        tabStates.innerHTML = states.map(s => {
          const reg = dgRegisters.find(r => {
            const n = String(r.name || '').toLowerCase();
            return s.keywords.every(k => n.includes(k));
          });
          const value = reg ? (reg.value === true ? "Yes" : reg.value === false ? "No" : reg.value ?? "—") : "—";
          return `<div style="padding:12px 0;border-bottom:1px solid #333;display:flex;justify-content:space-between;align-items:center;">
            <span style="color:#aaa;font-size:14px;">${s.label}</span>
            <span style="font-weight:bold;color:${reg ? s.color(reg.value) : '#666'}">${value}</span>
          </div>`;
        }).join("");

        const startReg = dgRegisters.find(r => /start/i.test(r.name));
        const stopReg = dgRegisters.find(r => /stop/i.test(r.name));
        const isRunning = dgRegisters.some(r => /status/i.test(r.name) && r.value > 0);

        tabControls.innerHTML = `
          <div style="text-align:center;margin:20px 0;">
            <div style="color:#aaa;margin-bottom:12px;">Current status</div>
            <div style="font-size:18px;font-weight:bold;color:${isRunning ? "#4caf50" : "#f44336"}">
              ${isRunning ? "RUNNING" : "STOPPED"}
            </div>
          </div>
          ${startReg ? `<button onclick="window.sendMqttCommand('${startReg._id}', '${startReg.deviceId}', 1)"
            style="width:100%;padding:16px;margin:8px 0;background:#4caf50;color:white;border:none;border-radius:12px;font-size:18px;font-weight:bold;">
            START
          </button>` : ""}
          ${stopReg ? `<button onclick="window.sendMqttCommand('${stopReg._id}', '${stopReg.deviceId}', 0)"
            style="width:100%;padding:16px;margin:8px 0;background:#f44336;color:white;border:none;border-radius:12px;font-size:18px;font-weight:bold;">
            STOP
          </button>` : ""}
        `;

      } else if (isSwitch) {
        document.querySelector('[data-tab="controls"]').style.display = "none";
        const switchLabel = labelCp.getAttribute("v:lbl") || "Switch";
        document.getElementById("modal-title").textContent = switchLabel;

        const switchRegisters = registers.filter(r => {
          const lbl = switchLabel.toLowerCase().replace(/\s+/g, '_');
          return r.name.toLowerCase().includes(lbl);
        });

        const states = [
          { label: "Communication Status", keywords: ["communication", "status"] },
          { label: "Operational Status", keywords: ["operational", "status"] },
          { label: "Switch Status", keywords: ["switch", "status"] },
        ];

        tabStates.innerHTML = states.map(s => {
          const reg = switchRegisters.find(r => {
            const n = String(r.name || '').toLowerCase();
            return s.keywords.every(k => n.includes(k));
          });
          const value = reg ? (reg.value > 0 ? "Closed" : "Open") : "—";
          const color = reg ? (reg.value > 0 ? "#4caf50" : "#f44336") : "#666";
          return `<div style="padding:12px 0;border-bottom:1px solid #333;display:flex;justify-content:space-between;align-items:center;">
            <span style="color:#aaa;font-size:14px;">${s.label}</span>
            <span style="font-weight:bold;color:${color}">${value}</span>
          </div>`;
        }).join("");
      }

      modal.style.display = "block";
      backdrop.style.display = "block";
    });
  }

  // === LEGACY FALLBACK (unchanged) ===
  svgRoot.querySelectorAll("text").forEach(textEl => {
    const text = textEl.textContent.trim();
    if (!text || /[0-9]/.test(text) || text.includes(":")) return;
    let register = findBestRegister(text);
    if (!register || register.value == null) return;
    const group = textEl.closest("g");
    if (group?.querySelector('v\\:cp[v\\:lbl]')) return;
    const formatted = formatRegValue(register, register.value);
    textEl.textContent = formatted;
    textEl.style.fill = "#075e1db9";
    textEl.style.fontWeight = "bold";
  });

  // === CLEANUP ===
  return {
    cleanup: () => {
      subscriptions.forEach(fn => fn?.());
      resetBtn.remove();
      container.removeEventListener("pointerdown", pointerDown);
      container.removeEventListener("pointerup", pointerUp);
      container.removeEventListener("pointerleave", pointerUp);
      modal.remove();
      backdrop.remove();
      console.log("[Schematics] Cleaned up");
    }
  };
}